import { useCombinedRefs } from '@visiba/react-std';
import { forwardRef, isValidElement, ReactNode, Ref, useEffect, useRef } from 'react';

interface AutoFocusProps {
	children: ReactNode;
}

function CellulaAutoFocus({ children }: AutoFocusProps, ref: Ref<HTMLDivElement>): JSX.Element {
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const internalRef = useRef<HTMLDivElement>(null);
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const combinedRefs = useCombinedRefs(ref, internalRef);

	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	useEffect(
		function whenMountedSetFocusToFirstChildElement(): void {
			// We need to check if the first element child is a HTMLElement with
			// .focus() method.
			if (internalRef.current?.firstElementChild && (internalRef.current.firstElementChild as HTMLElement)?.focus) {
				const firstChild = internalRef.current?.firstElementChild as HTMLElement;

				if (firstChild.tabIndex < 0) {
					firstChild.setAttribute('tabindex', '-1');
				}

				firstChild.focus();
			}
		},
		[ref],
	);

	return (
		<div style={{ display: 'contents' }} ref={combinedRefs}>
			{isValidElement(children) === false ? <span style={{ outline: 'none' }}>{children}</span> : children}
		</div>
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component to focus a specific element when mounted. Primarily used to help
 * assistive technology to present a better experience.
 *
 * This component will set focus to the first child HTMLElement. If that
 * HTMLElement is a non focusable element it will set tabindex to -1. In the
 * case when the first child isn't a HTMLElement, does not include a
 * `focus()`-method, then we will do nothing.
 */
const AutoFocus = forwardRef(CellulaAutoFocus);

export type { AutoFocusProps };
export { AutoFocus };
